/**
 * Email store — browser history sync for /emails/inbox deep links.
 * pushState on navigation; popstate restores folder / mailbox / thread / rules / compose.
 */
import {
  buildInboxSearchParams,
  parseInboxSearchParams,
  inboxUrlFromState,
} from '../inbox-url.js'

export function attachUrl(ctx) {
  ctx._suppressUrlSync = false

  ctx._inboxUrlState = function _inboxUrlState() {
    const rulesOpen = !!ctx.rulesOpen.value
    const threadOnly = !!ctx.threadOnlyView.value
    let view = null
    if (threadOnly) view = 'thread'
    else if (rulesOpen) view = 'rules'

    let compose = null
    if (!rulesOpen && ctx.composeOpen.value) {
      compose = ctx.composeType.value || 'new'
    }

    return {
      mailboxId: ctx.activeMailbox.value,
      folder: ctx.activeFolder.value || 'inbox',
      threadId: ctx.currentThreadId.value,
      view,
      compose,
    }
  }

  /**
   * Mirror store navigation into the address bar so refresh / share / back work.
   * Skipped while applying a URL (popstate / initial route) to avoid loops.
   */
  ctx.syncInboxUrl = function syncInboxUrl({ replace = false } = {}) {
    if (typeof window === 'undefined') return
    if (ctx._suppressUrlSync) return

    const next = inboxUrlFromState(ctx._inboxUrlState(), '/emails/inbox')
    const current = `${window.location.pathname}${window.location.search}`
    if (current === next) return

    if (replace) {
      window.history.replaceState({ emailInbox: true }, '', next)
    } else {
      window.history.pushState({ emailInbox: true }, '', next)
    }
  }

  /**
   * Apply current location.search to the store (popstate / Turbo reconnect).
   * Does not push history.
   */
  ctx.applyInboxUrlFromLocation = async function applyInboxUrlFromLocation(search = window.location.search) {
    if (typeof window === 'undefined') return

    const parsed = parseInboxSearchParams(search)
    const prevFolder = ctx.activeFolder.value
    const prevMailbox = ctx.activeMailbox.value
    const folderChanged = (parsed.folder || 'inbox') !== (prevFolder || 'inbox')
    const mailboxChanged = (parsed.mailboxId ?? null) !== (prevMailbox ?? null)

    ctx._suppressUrlSync = true
    try {
      ctx.batch(() => {
        ctx.activeFolder.value = parsed.folder || 'inbox'
        ctx.activeMailbox.value = parsed.mailboxId
        if (parsed.view === 'thread') ctx.threadOnlyView.value = true
      })

      if (parsed.view === 'rules') {
        ctx.openRules(parsed.mailboxId)
      } else if (ctx.rulesOpen.value) {
        ctx.closeRules()
      }

      if (folderChanged || mailboxChanged) {
        ctx.selectedIds.value = []
        if (!ctx.threadOnlyView.value) {
          await ctx.fetchEmails(1)
          ctx.fetchSidebarCounts()
        }
      }

      if (parsed.threadId) {
        if (ctx.currentThreadId.value !== parsed.threadId) {
          await ctx.selectThread(parsed.threadId)
        }
        if (parsed.compose && parsed.compose !== 'new') {
          await ctx.openCompose(parsed.compose, parsed.threadId)
        } else if (parsed.compose === 'new') {
          await ctx.openCompose('new')
        } else if (ctx.composeOpen.value) {
          ctx.closeCompose()
        }
      } else {
        if (ctx.currentThreadId.value) ctx.clearCurrentThread()
        if (parsed.compose === 'new') {
          await ctx.openCompose('new')
        } else if (ctx.composeOpen.value) {
          ctx.closeCompose()
        }
      }
    } finally {
      ctx._suppressUrlSync = false
    }
  }

  // Re-export builders for tests / Stimulus (optional).
  ctx.buildInboxSearchParams = buildInboxSearchParams
  ctx.parseInboxSearchParams = parseInboxSearchParams
  ctx.inboxUrlFromState = inboxUrlFromState
}
